import { Schema as S } from "effect";

import { ApiKey } from "./integrations";

/** Server `rhysomn-server-v2/src/routes/agentRoutes.ts` payloads. Extra keys are ignored. */

export const WaitingKind = S.Literals(["message", "context", "task"]);
export type WaitingKind = typeof WaitingKind.Type;

/** `from` is an api_key id (an agent) or a Clerk user id (a human). */
export const WaitingItem = S.Struct({
	kind: WaitingKind,
	from: S.String,
	sentAt: S.String,
	title: S.String,
	contextId: S.optionalKey(S.String),
	taskId: S.optionalKey(S.String),
});
export type WaitingItem = typeof WaitingItem.Type;

/** Every agent key appears, at 0 too; busiest first. */
export const AgentInbox = S.Struct({
	recipient: S.String,
	pending: S.Number,
	oldestFiledAt: S.NullOr(S.String),
	items: S.Array(WaitingItem),
});
export type AgentInbox = typeof AgentInbox.Type;
export const InboxResponse = S.Struct({ inbox: S.Array(AgentInbox) });

/** A filed mailbox row, newest first; `gone` marks a pointer to an expired or revoked context. */
export const SentItem = S.Struct({
	recipient: S.String,
	seq: S.Number,
	acked: S.Boolean,
	kind: WaitingKind,
	from: S.String,
	sentAt: S.String,
	title: S.String,
	gone: S.optionalKey(S.Boolean),
});
export type SentItem = typeof SentItem.Type;
export const SentResponse = S.Struct({ sent: S.Array(SentItem) });

export const PromoteTarget = S.Literals(["entry", "task"]);
export type PromoteTarget = typeof PromoteTarget.Type;

export const SharedContext = S.Struct({
	contextId: S.String,
	title: S.String,
	sender: S.String,
	recipients: S.Array(S.String),
	createdAt: S.String,
	/** Null for permanent retention. */
	expiresAt: S.NullOr(S.String),
	promoted: S.NullOr(
		S.Struct({ target: PromoteTarget, id: S.String, at: S.String }),
	),
});
export type SharedContext = typeof SharedContext.Type;
export const ContextsResponse = S.Struct({
	contexts: S.Array(SharedContext),
	expiresNext: S.NullOr(SharedContext),
});

/** One link the owner shared; the token never leaves the server after creation. */
export const AgentLinkSummary = S.Struct({
	id: S.String,
	/** The owner's private name for who holds it ("Dana"); never served. */
	label: S.String,
	agentId: S.String,
	forRecordId: S.NullOr(S.String),
	createdAt: S.String,
	lastUsedAt: S.NullOr(S.String),
	uses: S.Number,
	status: S.Literals(["active", "revoked"]),
	revokedAt: S.NullOr(S.String),
});
export type AgentLinkSummary = typeof AgentLinkSummary.Type;
export const LinksResponse = S.Struct({ links: S.Array(AgentLinkSummary) });
/** The only response that carries the link URL. */
export const CreatedLink = S.Struct({ link: AgentLinkSummary, url: S.String });

/** A person whose record holds someone else's agent link. `live` is null for outside links (not fetched). */
export const ReachableAgent = S.Struct({
	recordId: S.String,
	name: S.String,
	url: S.String,
	kind: S.Literals(["rhysomn", "external"]),
	live: S.NullOr(S.Boolean),
});
export type ReachableAgent = typeof ReachableAgent.Type;
export const ReachableResponse = S.Struct({
	reachable: S.Array(ReachableAgent),
});

/** A first send to a new outside origin, held for the owner. */
export const PendingSend = S.Struct({
	id: S.String,
	agentId: S.String,
	origin: S.String,
	to: S.String,
	recordId: S.NullOr(S.String),
	preview: S.String,
	createdAt: S.String,
	expiresAt: S.String,
});
export type PendingSend = typeof PendingSend.Type;
export const PendingResponse = S.Struct({ pending: S.Array(PendingSend) });

/** Approve sends the held message at once; `status` is the A2A task state, `error` a failed delivery. */
export const ApprovedSend = S.Struct({
	sent: S.Struct({
		id: S.String,
		status: S.String,
		error: S.optionalKey(S.String),
	}),
});
export type ApprovedSend = typeof ApprovedSend.Type;

/** Task states that mean the message did not reach the other agent. */
export const UNDELIVERED = ["failed", "rejected", "canceled", "unknown"];

/** Everything the Agents page shows, fetched together; `now` is the Clock at fetch time. */
export const AgentsData = S.Struct({
	now: S.Number,
	inbox: S.Array(AgentInbox),
	sent: S.Array(SentItem),
	contexts: S.Array(SharedContext),
	expiresNext: S.NullOr(SharedContext),
	keys: S.Array(ApiKey),
	links: S.Array(AgentLinkSummary),
	reachable: S.Array(ReachableAgent),
	pendingSends: S.Array(PendingSend),
});
export type AgentsData = typeof AgentsData.Type;
